import { Card, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { Application } from "../candidacies";
import { 
  Building, 
  CalendarDot, 
  UsersThree, 
  Dog, 
  Cigarette,
  User,
  Clock,
  CheckCircle,
  XCircle,
  PaperPlaneTilt,
  Trash
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { Button } from "@heroui/react";

interface CandidacyCardProps {
  candidacy: Application;
  onClick: () => void;
  onEmail?: (candidacy: Application, e: React.MouseEvent) => void;
  onDelete?: (candidacy: Application, e: React.MouseEvent) => void;
}

export const CandidacyCard = ({ candidacy, onClick, onEmail, onDelete }: CandidacyCardProps) => {
  const hasValidVisitDate = candidacy.visitDate && !isNaN(new Date(candidacy.visitDate).getTime());
  const hasValidCreateDate = candidacy.createDate && !isNaN(new Date(candidacy.createDate).getTime());
  
  // Calculate checklist progress
  const checkedItems = candidacy.checklist?.filter(c => c.isChecked).length || 0;
  const totalItems = candidacy.checklist?.length || 0;
  const checklistPercentage = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;

  // Handle card click but prevent if clicking on action buttons
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't trigger onClick if clicking on buttons or action area
    if (target.closest('button') || target.closest('[data-no-drag]')) {
      return;
    }
    onClick();
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all duration-200 border-neutral-200 hover:border-moss-primary/30"
      onClick={handleCardClick}
    >
      <CardContent className="p-3 space-y-2.5">
        {/* Name & Email */}
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <User weight="duotone" size={14} className="text-moss-primary shrink-0" />
            <h4 className="font-semibold text-sm text-gray-900 truncate">
              {candidacy.personalDetails.firstName} {candidacy.personalDetails.lastName}
            </h4>
          </div>
          <p className="text-xs text-gray-500 truncate ml-5">{candidacy.personalDetails.email}</p>
        </div>

        <Separator className="my-2" />

        {/* Unit */}
        <div className="flex items-center gap-1.5">
          <Building weight="duotone" size={14} className="text-moss-primary shrink-0" />
          <Badge variant="outline" className="text-xs bg-moss-primary/5 border-moss-primary/20 text-moss-primary">
            {candidacy.unitId}
          </Badge>
        </div>

        {/* Create Date */}
        {hasValidCreateDate && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Clock weight="duotone" size={14} className="shrink-0" />
            <span>Aangemaakt: {format(new Date(candidacy.createDate), 'dd/MM/yyyy')}</span>
          </div>
        )}

        {/* Visit Date */}
        {hasValidVisitDate && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <CalendarDot weight="duotone" size={14} className="shrink-0" />
            <span>Bezoek: {format(new Date(candidacy.visitDate), 'dd/MM/yyyy HH:mm')}</span>
          </div>
        )}

        <Separator className="my-2" />

        {/* Residents & Details */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-gray-600">
            <UsersThree weight="duotone" size={14} className="shrink-0" />
            <span>{candidacy.numberOfAdults + candidacy.numberOfChildren} personen</span>
          </div>
          
          <div className="flex items-center gap-2">
            {candidacy.hasPets && (
              <Badge variant="outline" className="gap-1 text-xs h-5 px-1.5">
                <Dog weight="duotone" size={12} />
                Huisdier
              </Badge>
            )}
            {candidacy.smoking && (
              <Badge variant="outline" className="gap-1 text-xs h-5 px-1.5">
                <Cigarette weight="duotone" size={12} />
                Roker
              </Badge>
            )}
          </div>
        </div>

        {/* Checklist Progress */}
        {totalItems > 0 && (
          <>
            <Separator className="my-2" />
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Checklist</span>
                <span className="font-medium text-gray-700">
                  {checkedItems}/{totalItems}
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-moss-primary rounded-full transition-all duration-300"
                  style={{ width: `${checklistPercentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle weight="fill" size={12} />
                  <span>{checkedItems} akkoord</span>
                </div>
                {(totalItems - checkedItems) > 0 && (
                  <div className="flex items-center gap-1 text-red-600">
                    <XCircle weight="fill" size={12} />
                    <span>{totalItems - checkedItems} niet akkoord</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Action Buttons */}
        {(onEmail || onDelete) && (
          <>
            <Separator className="my-2" />
            <div 
              className="flex items-center gap-2" 
              data-no-drag="true"
              onClick={(e) => e.stopPropagation()}
            >
              {onEmail && (
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  startContent={<PaperPlaneTilt size={14} weight="fill" />}
                  className="flex-1 h-7 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100"
                  onPress={() => {
                    const mockEvent = { stopPropagation: () => {}, preventDefault: () => {} } as React.MouseEvent;
                    onEmail(candidacy, mockEvent);
                  }}
                >
                  E-mail
                </Button>
              )}
              {onDelete && (
                <Button
                  size="sm"
                  variant="flat"
                  color="danger"
                  startContent={<Trash size={14} weight="fill" />}
                  className="flex-1 h-7 text-xs bg-red-50 text-red-700 hover:bg-red-100"
                  onPress={() => {
                    const mockEvent = { stopPropagation: () => {}, preventDefault: () => {} } as React.MouseEvent;
                    onDelete(candidacy, mockEvent);
                  }}
                >
                  Verwijderen
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
